import React, { useState } from "react";
import { toast } from "react-toastify";
import { useCustomerVerifyInstallationNoIssueMutation, useCustomerVerifyInstallationWithIssueMutation } from "../../../../service/order-service";
import styles from "../solar-journey.module.scss";

interface Props {
	orderId: string;
	onCancel: () => void;
	onSuccess: (type: "congratulations" | "feedback") => void;
}

const ISSUE_TYPES = [
	"Visible Damage On Panel",
	"Inverter Issues",
	"Battery Issues",
	"Roof Damage And Leaks",
	"Decreased Energy Production",
	"Other",
];

const CornerCheck = () => (
	<div className={styles.cornerCheckBadge}>
		<svg width="7" height="6" viewBox="0 0 7 6" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path d="M1 3L2.79 4.5L6 1" stroke="var(--app-base-white)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
		</svg>
	</div>
);

const StarIcon: React.FC<{ filled: boolean; onClick: () => void }> = ({ filled, onClick }) => (
	<svg
		width="22"
		height="21"
		viewBox="0 0 22 21"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		onClick={onClick}
		style={{ cursor: "pointer" }}
	>
		<path
			d="M11 0L13.47 7.6L21.36 7.6L14.95 12.3L17.41 19.9L11 15.2L4.59 19.9L7.05 12.3L0.64 7.6L8.53 7.6L11 0Z"
			fill={filled ? "var(--app-landing-orange)" : "rgba(249, 160, 27, 0.07)"}
		/>
	</svg>
);

export const AttestationPage: React.FC<Props> = ({ orderId, onCancel, onSuccess }) => {
	const [confirmed, setConfirmed] = useState<boolean | null>(null);
	const [comment, setComment] = useState("");
	const [rating, setRating] = useState(0);
	const [selectedIssue, setSelectedIssue] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);

	const [verifyNoIssue] = useCustomerVerifyInstallationNoIssueMutation();
	const [verifyWithIssue] = useCustomerVerifyInstallationWithIssueMutation();

	const isFormValid = () => {
		if (confirmed === null) return false;
		if (confirmed && rating < 2) return false;
		if (!confirmed && !selectedIssue) return false;
		return true;
	};

	const handleSubmit = async () => {
		if (!isFormValid() || isSubmitting) return;

		setIsSubmitting(true);
		try {
			if (confirmed) {
				await verifyNoIssue({ orderId, rating }).unwrap();
			} else {
				await verifyWithIssue({
					orderId,
					type: selectedIssue,
					comment: selectedIssue === "Other" ? comment : "",
				}).unwrap();
			}
			onSuccess(confirmed ? "congratulations" : "feedback");
		} catch (err: any) {
			toast.error(err?.data?.title || "Something went wrong. Please try again.");
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<div className={styles.attestationContainer}>
			<div className={styles.attestationHeader}>
				<h3 className={styles.attestationTitle}>Attestation</h3>
				<span className={styles.attestationSubtitle}>
					Your journey to clean energy is complete. Enjoy sustainable power and savings
				</span>
			</div>

			<div className={styles.attestationQuestion}>
				Do you confirm that the solar system was installed correctly and that no damage was caused during the installation?
			</div>

			<div className={styles.toggleRow}>
				<button
					className={`${styles.toggleButton} ${confirmed === true ? styles.toggleActive : ""}`}
					onClick={() => { setConfirmed(true); setSelectedIssue(""); }}
				>
					{confirmed === true && <CornerCheck />}
					Yes
				</button>
				<button
					className={`${styles.toggleButton} ${confirmed === false ? styles.toggleActive : ""}`}
					onClick={() => { setConfirmed(false); setRating(0); }}
				>
					{confirmed === false && <CornerCheck />}
					No
				</button>
			</div>

			{confirmed === false && (
				<div className={styles.attestationField}>
					<label className={styles.attestationLabel}>Common Issues</label>
					<select
						className={styles.attestationSelect}
						value={selectedIssue}
						onChange={e => setSelectedIssue(e.target.value)}
					>
						<option value="">Select an issue</option>
						{ISSUE_TYPES.map(type => (
							<option key={type} value={type}>{type}</option>
						))}
					</select>
				</div>
			)}

			{confirmed === false && selectedIssue === "Other" && (
				<div className={styles.attestationField}>
					<label className={styles.attestationLabel}>Comment (optional)</label>
					<textarea
						className={styles.attestationTextarea}
						placeholder="Describe the issue you encountered during installation..."
						value={comment}
						onChange={e => setComment(e.target.value)}
						rows={6}
					/>
				</div>
			)}

			{confirmed === true && (
				<>
					<div className={styles.attestationField}>
						<label className={styles.attestationLabel}>Comment (optional)</label>
						<textarea
							className={styles.attestationTextarea}
							placeholder="I hereby confirm that the solar system has been installed properly and that no damage was caused during the installation process."
							value={comment}
							onChange={e => setComment(e.target.value)}
							rows={6}
						/>
					</div>

					<div className={styles.ratingRow}>
						<span className={styles.attestationLabel}>Rate this service</span>
						<div className={styles.starRow}>
							{[1, 2, 3, 4, 5].map(star => (
								<StarIcon
									key={star}
									filled={star <= rating}
									onClick={() => setRating(star)}
								/>
							))}
						</div>
					</div>
				</>
			)}

			<div className={styles.attestationFooter}>
				<div className={styles.attestationFooterInner}>
					<button className={styles.attestationCancelButton} onClick={onCancel}>
						Cancel
					</button>
					<button
						className={styles.attestationSubmitButton}
						disabled={!isFormValid() || isSubmitting}
						onClick={handleSubmit}
					>
						{isSubmitting ? "Submitting..." : "Attest"}
					</button>
				</div>
			</div>
		</div>
	);
};
